import React from "react";
import { authorName, formatTime, initials, splitMentions } from "./utils.js";
import "./CommentToast.css";

/**
 * Плашки комментариев поверх видео (снизу, над таймлайном).
 * Компактная: ширина по содержимому. Строка 1 — аватар, автор, билетики тегов, крестик;
 * строка 2 — звезда-маркер, таймкод, текст. Звезда-маркер — тот же вектор, что на таймлайне (starIconPurple.svg),
 * залита цветом тега.
 *
 * Класть внутрь контейнера плеера с position: relative (в 7cut — .video-container в VideoWithHls).
 *
 * props:
 *   toasts      из useCommentToasts
 *   onOpen      (comment) => void — клик по плашке (подсветить комментарий в списке)
 *   onDismiss   (id) => void
 *   onHold / onRelease  (id) => void — заморозка таймера при наведении
 *   bottom      отступ от нижнего края контейнера, px (16)
 *   tagsDefaultColors  { [tagId]: "#hex" } — как в CommentsBlock, если у тега нет color
 */
export default function CommentToast({ toasts, onOpen, onDismiss, onHold, onRelease, bottom = 16, tagsDefaultColors = {} }) {
  if (!toasts?.length) return null;
  return (
    <div className="ctoast-stack" style={{ bottom }} role="status" aria-live="polite">
      {toasts.map((t) => {
        const c = t.comment;
        const tags = (c.tags || []).map((tag) => ({ ...tag, color: tag.color || tagsDefaultColors[tag.id] || "#9a6aff" }));
        const accent = tags[0]?.color || "#9a6aff";
        const name = authorName(c.author);
        return (
          <div
            key={t.id}
            className={`ctoast ${t.leaving ? "ctoast--leaving" : ""}`}
            onMouseEnter={() => onHold?.(t.id)}
            onMouseLeave={() => onRelease?.(t.id)}
            onClick={() => onOpen?.(c)}
          >
            <div className="ctoast__header">
              <Avatar author={c.author} name={name} />
              <span className="ctoast__author">{name}</span>
              {tags.map((tag) => (
                <span key={tag.id} className="ctoast__ticket" style={{ "--tk-color": tag.color }}>
                  {tag.name || tag.title || ""}
                </span>
              ))}
              {onDismiss && (
                <button
                  type="button"
                  className="ctoast__close"
                  aria-label="Скрыть"
                  onClick={(e) => { e.stopPropagation(); onDismiss(t.id); }}
                >
                  ×
                </button>
              )}
            </div>

            <div className="ctoast__line">
              <Sparkle color={accent} />
              <span className="ctoast__timecode">
                {formatTime(t.start)}{t.end != null ? ` – ${formatTime(t.end)}` : ""}
              </span>
              <span className="ctoast__text">
                {splitMentions(c.content).map((p, i) =>
                  p.mention ? <span key={i} className="ctoast__mention">{p.text}</span> : <React.Fragment key={i}>{p.text}</React.Fragment>
                )}
              </span>
            </div>

            {c.image && <img className="ctoast__thumb" src={c.image} alt="" />}
          </div>
        );
      })}
    </div>
  );
}

/** Аватар как в components/Avatar: фото, иначе пастельный круг с инициалами. */
function Avatar({ author, name }) {
  const bg = pastelColor(name);
  return (
    <span className="ctoast__avatar" style={{ backgroundColor: bg }}>
      {author?.avatar ? <img src={author.avatar} alt="" /> : initials(author)}
    </span>
  );
}

// Та же формула, что в Avatar.js
function pastelColor(text) {
  if (!text) return "#FFFFFF";
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${hash % 360}, 70%, 85%)`;
}

/** Звезда-маркер с таймлайна — вектор из src/assets/starIconPurple.svg, не поворачивать. */
export function Sparkle({ color = "#9a6aff", size = 14 }) {
  return (
    <svg className="ctoast__sparkle" width={size} height={size} viewBox="27 19 48 64" aria-hidden="true">
      <path
        fill={color}
        d="M70.65,47.05l-10.19-3.8c-.52-.26-1.03-.79-1.11-1.04l-4.13-17.73c-.46-1.84-2.19-2.67-3.69-2.67s-3.23.83-3.69,2.67l-4.27,17.7c-.12.35-.5.83-.92,1.05l-10.23,3.82c-1.52.56-2.5,1.97-2.5,3.6s.98,3.04,2.5,3.6l10.19,3.8c.48.24.85.74.96,1.04l4.28,17.73c.47,1.77,2.2,2.55,3.75,2.55.04,0,.08,0,.11,0,1.82-.05,3.2-1.05,3.52-2.56l4.11-17.68c.1-.3.6-.82,1.08-1.07l10.23-3.82c1.52-.56,2.5-1.97,2.5-3.6s-.98-3.04-2.5-3.6Z"
      />
    </svg>
  );
}
